import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, UserCheck, UserX, UserPlus, Search, Filter, X, ChevronDown, MapPin, Trash2, ExternalLink } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmployeeAffiliationEditor } from '@/components/EmployeeAffiliationEditor';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Button } from '@/components/Button';
import { formatEmployeeName } from '@/lib/format';
import { useLabel } from '@/lib/labels';

// v0.3 (docs/design/v03-user-employee-refinement.md §2-6, §5-2): 社員の所属編集 + 連動先userへの反映。
// 2026-08-05 テストレビューCRITICAL対応: バックエンドのPATCH /employees/:id/node-assignments
// (dry-run/confirm 2phase) は実装済みだったが、これを呼び出すUI自体が存在しなかったため新設する。
type AssigningState = {
  employee_id: string;
  name: string;
};

/**
 * `/employees` (社員管理画面、v0.2.5 S-7バックエンド実装済みだがフロント未実装だった問題への対応)
 * 設計: docs/design/v025-store-node-hierarchy.md「社員 (employees) — SCD-2完全履歴化方針」節、「登録フロー」節
 * 権限: isCompanyAdmin 必須 (バックエンドで強制、本画面はナビ非表示 + 403で保護する既存方針に合わせる)
 *
 * Designレビュー (2026-08-03) 反映: 赤(brand-red)は「社員追加」CTAと退職確認ダイアログの実行ボタンのみに絞り、
 * 行内アイコン・リンクは中立色 (text-text-sub / text-chrome-base) にした (docs/design/ui-guidelines.md §2.2)。
 * select は appearance-none + 自前chevronでスタイル化 (§7.2)。
 */
type EmployeeRow = {
  employee_id: string;
  default_user_id: string | null;
  /** TD-15: default_user_id は新規ユーザー作成時の自動リンクが廃止されたため更新されない。
   *  CONTROL_DB.users を実際に検索した値 (routes/employees.ts) を使う。 */
  has_linked_user: boolean;
  first_hire_date: string;
  employee_number: string;
  family_name: string;
  given_name: string;
  family_name_kana: string | null;
  given_name_kana: string | null;
  middle_name: string | null;
  middle_name_kana: string | null;
  employee_category_id: string | null;
  employment_type_id: string | null;
  hire_date: string;
  termination_date: string | null;
  status: string; // 'active' | 'terminated'
  effective_from: string;
};

type Category = { category_id: string; code: string; label: string };
type EmploymentType = { type_id: string; code: string; label: string };

type CreateForm = {
  employee_number: string;
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  middle_name: string;
  middle_name_kana: string;
  employee_category_id: string;
  employment_type_id: string;
  hire_date: string;
};

type EditForm = {
  employee_id: string;
  employee_number: string;
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  middle_name: string;
  middle_name_kana: string;
  employee_category_id: string;
  employment_type_id: string;
  hire_date: string;
  effective_from: string;
  wasTerminated: boolean;
};

const today = () => new Date().toISOString().slice(0, 10);

// §7.2 フォーム部品: 赤いフォーカスリングを使わず共通の focus-ring (chrome.base) に統一するための
// 軽量ローカルヘルパー (このページ内でテキスト系入力が10箇所超あるため、CLAUDE.md §6d「4回以上で抽象化」に該当)
function TextField({
  id, label, required, type = 'text', value, onChange, placeholder, mono, hint, autoFocus,
}: {
  id: string; label: string; required?: boolean; type?: string; value: string;
  onChange: (v: string) => void; placeholder?: string; mono?: boolean; hint?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-neutral-sub mb-1">
        {label}{required && <span className="text-danger-text"> *</span>}
      </label>
      <input
        id={id} required={required} type={type} autoFocus={autoFocus} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2 rounded border border-neutral-line focus:outline-none focus-ring ${mono ? 'font-mono' : ''}`}
      />
      {hint && <p className="text-xs text-neutral-sub mt-1">{hint}</p>}
    </div>
  );
}

function SelectField({
  id, label, value, onChange, options, placeholder = '— 未設定 —',
}: {
  id: string; label: string; value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>; placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-neutral-sub mb-1">{label}</label>
      <div className="relative">
        <select
          id={id} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none px-3 py-2 pr-8 rounded border border-neutral-line focus:outline-none focus:shadow-focus-ring bg-white"
        >
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-sub" aria-hidden />
      </div>
    </div>
  );
}

export function EmployeesPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: employees = [], isLoading } = useQuery<EmployeeRow[]>({
    queryKey: ['employees'],
    queryFn: () => apiFetch<EmployeeRow[]>('/employees'),
  });
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['employee-categories'],
    queryFn: () => apiFetch<Category[]>('/employee-categories'),
  });
  const { data: employmentTypes = [] } = useQuery<EmploymentType[]>({
    queryKey: ['employment-types'],
    queryFn: () => apiFetch<EmploymentType[]>('/employment-types'),
  });

  const categoryLabel = (id: string | null) => (id ? categories.find((c) => c.category_id === id)?.label ?? '—' : '—');
  const employmentTypeLabel = (id: string | null) => (id ? employmentTypes.find((t) => t.type_id === id)?.label ?? '—' : '—');
  const categoryOptions = categories.map((c) => ({ value: c.category_id, label: c.label }));
  const employmentTypeOptions = employmentTypes.map((et) => ({ value: et.type_id, label: et.label }));

  const [creating, setCreating] = useState<CreateForm | null>(null);
  const [editing, setEditing] = useState<EditForm | null>(null);
  const [retiring, setRetiring] = useState<EmployeeRow | null>(null);
  const [purging, setPurging] = useState<EmployeeRow | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'terminated'>('all');

  // v0.3 (§2-6 所属編集)。編集 UI 本体は EmployeeAffiliationEditor が持つ。
  const [assigning, setAssigning] = useState<AssigningState | null>(null);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return employees.filter((e) => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (q && !(
        `${e.family_name}${e.middle_name ?? ''}${e.given_name}`.toLowerCase().includes(q) ||
        `${e.family_name_kana ?? ''}${e.middle_name_kana ?? ''}${e.given_name_kana ?? ''}`.toLowerCase().includes(q) ||
        e.employee_number.toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [employees, searchText, filterStatus]);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort<EmployeeRow>(filtered, {
    defaultKey: 'family_name',
    defaultDir: 'asc',
    accessors: {
      family_name: (e) => `${e.family_name}${e.given_name}`,
      employee_number: (e) => e.employee_number,
      employee_category_id: (e) => categoryLabel(e.employee_category_id),
      employment_type_id: (e) => employmentTypeLabel(e.employment_type_id),
      hire_date: (e) => e.hire_date,
      status: (e) => e.status,
    },
  });

  const hasAnyFilter = Boolean(searchText || filterStatus !== 'all');
  const clearFilters = () => { setSearchText(''); setFilterStatus('all'); };

  const createMut = useMutation({
    mutationFn: (f: CreateForm) => apiFetch('/employees', {
      method: 'POST',
      body: JSON.stringify({
        employee_number: f.employee_number.trim(),
        family_name: f.family_name.trim(),
        given_name: f.given_name.trim(),
        family_name_kana: f.family_name_kana.trim() || undefined,
        given_name_kana: f.given_name_kana.trim() || undefined,
        middle_name: f.middle_name.trim() || undefined,
        middle_name_kana: f.middle_name_kana.trim() || undefined,
        employee_category_id: f.employee_category_id || null,
        employment_type_id: f.employment_type_id || null,
        hire_date: f.hire_date,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      setCreating(null);
      toast.success('社員を登録しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '登録に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: (f: EditForm) => apiFetch(`/employees/${f.employee_id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        employee_number: f.employee_number.trim(),
        family_name: f.family_name.trim(),
        given_name: f.given_name.trim(),
        family_name_kana: f.family_name_kana.trim() || null,
        given_name_kana: f.given_name_kana.trim() || null,
        middle_name: f.middle_name.trim() || null,
        middle_name_kana: f.middle_name_kana.trim() || null,
        employee_category_id: f.employee_category_id || null,
        employment_type_id: f.employment_type_id || null,
        hire_date: f.hire_date,
        effective_from: f.effective_from,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      setEditing(null);
      toast.success('社員情報を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  // 所属編集はモーダル内の EmployeeAffiliationEditor が現在値の取得・保存・連動確認まで担う。
  const startAssigning = (e: EmployeeRow) => {
    setAssigning({ employee_id: e.employee_id, name: `${e.family_name} ${e.given_name}` });
  };

  const retireMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/employees/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      const name = retiring ? `${retiring.family_name}${retiring.given_name}` : '';
      setRetiring(null);
      toast.success(`「${name}」を退職処理しました`);
    },
    onError: (e) => {
      // Designレビュー指摘 (HIGH): 失敗時にダイアログを閉じない (UsersPage.tsx の deleteMut.onError と
      // 同じパターン)。閉じてしまうとエラー内容を読む間もなくリトライ手段が消える
      toast.error(e instanceof ApiClientError ? e.message : '退職処理に失敗しました');
    },
  });

  // 誤って作成した社員の完全削除 (退職処理とは別、物理削除・復元不可)
  const purgeMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/employees/${id}/purge`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      const name = purging ? `${purging.family_name}${purging.given_name}` : '';
      setPurging(null);
      toast.success(`「${name}」を削除しました`);
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    },
  });

  const statusChip = (status: string) => (
    status === 'active' ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-success-bg text-success-text">
        <UserCheck size={12} /> 在職
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-surface-sunken text-text-sub">
        <UserX size={12} /> 退職
      </span>
    )
  );

  const startEdit = (e: EmployeeRow) => {
    setEditing({
      employee_id: e.employee_id,
      employee_number: e.employee_number,
      family_name: e.family_name,
      given_name: e.given_name,
      family_name_kana: e.family_name_kana ?? '',
      given_name_kana: e.given_name_kana ?? '',
      middle_name: e.middle_name ?? '',
      middle_name_kana: e.middle_name_kana ?? '',
      employee_category_id: e.employee_category_id ?? '',
      employment_type_id: e.employment_type_id ?? '',
      hire_date: e.hire_date,
      effective_from: today(),
      wasTerminated: e.status === 'terminated',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1">{t('entity.employees', '社員管理')}</h1>
          <p className="text-sm text-neutral-sub mt-1">
            社員の氏名・区分・雇用形態を登録します
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus size={16} />}
          onClick={() => setCreating({
            employee_number: '', family_name: '', given_name: '', family_name_kana: '', given_name_kana: '',
            middle_name: '', middle_name_kana: '',
            employee_category_id: '', employment_type_id: '', hire_date: today(),
          })}
        >
          社員追加
        </Button>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-neutral-sub" />
        <div className="relative">
          <select
            aria-label="状態で絞り込み"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'terminated')}
            className="appearance-none px-2 py-1 pr-7 border border-neutral-line rounded text-sm focus:outline-none focus:shadow-focus-ring bg-white"
          >
            <option value="all">全ての状態</option>
            <option value="active">在職のみ</option>
            <option value="terminated">退職のみ</option>
          </select>
          <ChevronDown size={13} className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-neutral-sub" aria-hidden />
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub pointer-events-none" />
          <label htmlFor="employee-search" className="sr-only">氏名・かな・社員番号で検索</label>
          <input id="employee-search" type="text" value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="氏名・かな・社員番号で検索"
            className="pl-6 pr-2 py-1 border border-neutral-line rounded text-sm w-52 focus:outline-none focus-ring" />
        </div>
        <div className="flex-1" />
        <div className="text-xs text-neutral-sub tabular-nums whitespace-nowrap">
          {sortedRows.length} 件表示中（全 {employees.length} 件）
        </div>
        {hasAnyFilter && (
          <button type="button" onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-neutral-sub hover:text-text-main">
            <X size={12} /> 絞込みをクリア
          </button>
        )}
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <SortableTh sortKey="family_name" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>氏名</SortableTh>
                <SortableTh sortKey="employee_number" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-32">{t('field.employees.employee_number', '社員番号')}</SortableTh>
                <SortableTh sortKey="employee_category_id" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-32">{t('field.employees.employee_category_id', '社員区分')}</SortableTh>
                <SortableTh sortKey="employment_type_id" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-32">{t('field.employees.employment_type_id', '雇用形態')}</SortableTh>
                <SortableTh sortKey="hire_date" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-28">{t('field.employees.hire_date', '入社日')}</SortableTh>
                <SortableTh sortKey="status" currentKey={sortKey} dir={sortDir} onClick={toggleSort} className="w-20">状態</SortableTh>
                <th scope="col" className="w-44 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((e) => (
                <tr key={e.employee_id} className="border-t border-neutral-line hover:bg-neutral-panel">
                  <td className="px-4 py-3 font-medium">
                    {formatEmployeeName(e)}
                    {(e.family_name_kana || e.given_name_kana) && (
                      <div className="text-xs text-neutral-sub font-normal">
                        {formatEmployeeName({ family_name: e.family_name_kana ?? '', middle_name: e.middle_name_kana, given_name: e.given_name_kana ?? '' })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.employee_number}</td>
                  <td className="px-4 py-3">{categoryLabel(e.employee_category_id)}</td>
                  <td className="px-4 py-3">{employmentTypeLabel(e.employment_type_id)}</td>
                  <td className="px-4 py-3 tabular-nums text-neutral-sub text-xs">{e.hire_date}</td>
                  <td className="px-4 py-3">{statusChip(e.status)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <Link
                        to={`/employees/${e.employee_id}`}
                        className="inline-flex items-center gap-1 text-xs text-chrome-base hover:underline"
                        title="詳細 (所属・スキル・単価・年収の壁)"
                      >
                        <ExternalLink size={12} /> 詳細
                      </Link>
                      {!e.has_linked_user && e.status === 'active' && (
                        <Link
                          to={`/users?create_for_employee=${e.employee_id}&employee_name=${encodeURIComponent(formatEmployeeName(e))}&employee_family_name=${encodeURIComponent(e.family_name)}&employee_given_name=${encodeURIComponent(e.given_name)}&employee_middle_name=${encodeURIComponent(e.middle_name ?? '')}`}
                          className="inline-flex items-center gap-1 text-xs text-chrome-base hover:underline"
                          title="この社員のログインアカウントを作成する"
                        >
                          <UserPlus size={12} /> アカウント作成
                        </Link>
                      )}
                      <button type="button" aria-label="編集" onClick={() => startEdit(e)}
                        className="p-1.5 rounded-md text-text-sub hover:text-text-main hover:bg-surface-sunken transition-colors"
                        title="編集"
                      >
                        <Pencil size={14} />
                      </button>
                      <button type="button" aria-label="所属編集" onClick={() => startAssigning(e)}
                        className="p-1.5 rounded-md text-text-sub hover:text-text-main hover:bg-surface-sunken transition-colors"
                        title="所属編集"
                      >
                        <MapPin size={14} />
                      </button>
                      <button type="button"
                        aria-label={e.status === 'terminated' ? '既に退職済みです' : '退職処理'}
                        onClick={() => setRetiring(e)}
                        disabled={e.status === 'terminated'}
                        className="p-1.5 rounded-md text-text-sub hover:text-danger-text hover:bg-surface-sunken transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={e.status === 'terminated' ? '既に退職済みです' : '退職処理'}
                      >
                        <UserX size={14} />
                      </button>
                      <button type="button" aria-label="完全削除" onClick={() => setPurging(e)}
                        className="p-1.5 rounded-md text-text-sub hover:text-danger-text hover:bg-surface-sunken transition-colors border-l border-neutral-line pl-2.5"
                        title="完全削除（誤って作成した社員の取消用、退職者には使わないでください）"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedRows.length === 0 && (
            <EmptyState
              message={hasAnyFilter ? 'フィルタ条件に一致する社員がいません' : '社員が登録されていません'}
              description={hasAnyFilter ? '絞込条件を変更するか、クリアして再表示してください' : '「社員追加」から登録してください'}
              actionLabel={hasAnyFilter ? '絞込みをクリア' : undefined}
              onAction={hasAnyFilter ? clearFilters : undefined}
            />
          )}
        </div>
      )}

      {/* 新規登録モーダル */}
      <Modal
        open={creating !== null}
        title="社員登録"
        onClose={() => setCreating(null)}
        footer={creating && (
          <>
            <Button variant="secondary" onClick={() => setCreating(null)}>キャンセル</Button>
            <Button variant="primary" type="submit" form="employee-create-form"
              loading={createMut.isPending} loadingLabel="登録中…">
              登録
            </Button>
          </>
        )}
      >
        {creating && (
          <form id="employee-create-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!creating.employee_number.trim()) { toast.error('社員番号を入力してください'); return; }
              if (!creating.family_name.trim() || !creating.given_name.trim()) { toast.error('氏名を入力してください'); return; }
              createMut.mutate(creating);
            }}
            className="space-y-3"
          >
            <p className="text-xs text-neutral-sub">姓 (Family) → ミドル (任意) → 名 (Given) の順で入力してください</p>
            <div className="grid grid-cols-[5fr_2fr_5fr] gap-3">
              <TextField id="employee-create-family-name" label={t('field.employees.family_name', '姓 (Family)')} required autoFocus
                value={creating.family_name} onChange={(v) => setCreating({ ...creating, family_name: v })} />
              <TextField id="employee-create-middle-name" label={t('field.employees.middle_name', 'ミドル (任意)')}
                value={creating.middle_name} onChange={(v) => setCreating({ ...creating, middle_name: v })} />
              <TextField id="employee-create-given-name" label={t('field.employees.given_name', '名 (Given)')} required
                value={creating.given_name} onChange={(v) => setCreating({ ...creating, given_name: v })} />
            </div>
            <div className="grid grid-cols-[5fr_2fr_5fr] gap-3">
              <TextField id="employee-create-family-kana" label={t('field.employees.family_name_kana', '姓（かな）')}
                value={creating.family_name_kana} onChange={(v) => setCreating({ ...creating, family_name_kana: v })} />
              <TextField id="employee-create-middle-kana" label={t('field.employees.middle_name_kana', 'ミドルかな（任意）')}
                value={creating.middle_name_kana} onChange={(v) => setCreating({ ...creating, middle_name_kana: v })} />
              <TextField id="employee-create-given-kana" label={t('field.employees.given_name_kana', '名（かな）')}
                value={creating.given_name_kana} onChange={(v) => setCreating({ ...creating, given_name_kana: v })} />
            </div>
            <TextField id="employee-create-number" label={t('field.employees.employee_number', '社員番号')} required mono placeholder="例：E-0012"
              value={creating.employee_number} onChange={(v) => setCreating({ ...creating, employee_number: v })} />
            <div className="grid grid-cols-2 gap-3">
              <SelectField id="employee-create-category" label={t('field.employees.employee_category_id', '社員区分')} options={categoryOptions}
                value={creating.employee_category_id} onChange={(v) => setCreating({ ...creating, employee_category_id: v })} />
              <SelectField id="employee-create-employment-type" label={t('field.employees.employment_type_id', '雇用形態')} options={employmentTypeOptions}
                value={creating.employment_type_id} onChange={(v) => setCreating({ ...creating, employment_type_id: v })} />
            </div>
            <TextField id="employee-create-hire-date" label={t('field.employees.hire_date', '入社日')} required type="date"
              value={creating.hire_date} onChange={(v) => setCreating({ ...creating, hire_date: v })} />
          </form>
        )}
      </Modal>

      {/* 編集モーダル */}
      <Modal
        open={editing !== null}
        title="社員情報を編集"
        onClose={() => setEditing(null)}
        footer={editing && (
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>キャンセル</Button>
            <Button variant="primary" type="submit" form="employee-edit-form"
              loading={updateMut.isPending} loadingLabel="保存中…">
              保存
            </Button>
          </>
        )}
      >
        {editing && (
          <form id="employee-edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!editing.family_name.trim() || !editing.given_name.trim()) { toast.error('氏名を入力してください'); return; }
              updateMut.mutate(editing);
            }}
            className="space-y-3"
          >
            {editing.wasTerminated && (
              <p className="text-xs text-alert-text bg-alert-bg border border-alert-border/40 rounded p-2">
                この社員は退職済みです。この画面で保存すると（かな等の軽微な修正のみでも）再雇用として履歴に記録され「在職」に戻ります。
                退職記録をそのまま残したい場合は保存しないでください。
              </p>
            )}
            <p className="text-xs text-neutral-sub">姓 (Family) → ミドル (任意) → 名 (Given) の順で入力してください</p>
            <div className="grid grid-cols-[5fr_2fr_5fr] gap-3">
              <TextField id="employee-edit-family-name" label={t('field.employees.family_name', '姓 (Family)')} required autoFocus
                value={editing.family_name} onChange={(v) => setEditing({ ...editing, family_name: v })} />
              <TextField id="employee-edit-middle-name" label={t('field.employees.middle_name', 'ミドル (任意)')}
                value={editing.middle_name} onChange={(v) => setEditing({ ...editing, middle_name: v })} />
              <TextField id="employee-edit-given-name" label={t('field.employees.given_name', '名 (Given)')} required
                value={editing.given_name} onChange={(v) => setEditing({ ...editing, given_name: v })} />
            </div>
            <div className="grid grid-cols-[5fr_2fr_5fr] gap-3">
              <TextField id="employee-edit-family-kana" label={t('field.employees.family_name_kana', '姓（かな）')}
                value={editing.family_name_kana} onChange={(v) => setEditing({ ...editing, family_name_kana: v })} />
              <TextField id="employee-edit-middle-kana" label={t('field.employees.middle_name_kana', 'ミドルかな（任意）')}
                value={editing.middle_name_kana} onChange={(v) => setEditing({ ...editing, middle_name_kana: v })} />
              <TextField id="employee-edit-given-kana" label={t('field.employees.given_name_kana', '名（かな）')}
                value={editing.given_name_kana} onChange={(v) => setEditing({ ...editing, given_name_kana: v })} />
            </div>
            <TextField id="employee-edit-number" label={t('field.employees.employee_number', '社員番号')} required mono
              value={editing.employee_number} onChange={(v) => setEditing({ ...editing, employee_number: v })} />
            <div className="grid grid-cols-2 gap-3">
              <SelectField id="employee-edit-category" label={t('field.employees.employee_category_id', '社員区分')} options={categoryOptions}
                value={editing.employee_category_id} onChange={(v) => setEditing({ ...editing, employee_category_id: v })} />
              <SelectField id="employee-edit-employment-type" label={t('field.employees.employment_type_id', '雇用形態')} options={employmentTypeOptions}
                value={editing.employment_type_id} onChange={(v) => setEditing({ ...editing, employment_type_id: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField id="employee-edit-hire-date" label={t('field.employees.hire_date', '入社日')} required type="date"
                value={editing.hire_date} onChange={(v) => setEditing({ ...editing, hire_date: v })} />
              <TextField id="employee-edit-effective-from" label="稼働開始日" required type="date"
                hint="この変更が反映される日付（通常は本日）"
                value={editing.effective_from} onChange={(v) => setEditing({ ...editing, effective_from: v })} />
            </div>
          </form>
        )}
      </Modal>

      {/* 退職確認ダイアログ */}
      <ConfirmDialog
        open={retiring !== null}
        title="退職処理を行いますか？"
        destructive
        confirmLabel="退職処理する"
        cancelLabel="キャンセル"
        loading={retireMut.isPending}
        onClose={() => { if (!retireMut.isPending) setRetiring(null); }}
        onConfirm={() => { if (retiring) retireMut.mutate(retiring.employee_id); }}
        message={retiring && (
          <div className="space-y-2">
            <p>
              以下の社員を本日付で退職処理します。データは削除されず、履歴として残ります。
            </p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">氏名：</span><span className="font-medium">{retiring.family_name} {retiring.given_name}</span></div>
              <div><span className="text-neutral-sub">社員番号：</span><span className="font-mono">{retiring.employee_number}</span></div>
            </div>
          </div>
        )}
      />

      {/* 完全削除確認ダイアログ (誤登録の取消専用、退職処理とは別、物理削除・復元不可) */}
      <ConfirmDialog
        open={purging !== null}
        title="この社員を完全に削除しますか？"
        destructive
        confirmLabel="完全削除する"
        cancelLabel="キャンセル"
        loading={purgeMut.isPending}
        onClose={() => { if (!purgeMut.isPending) setPurging(null); }}
        onConfirm={() => { if (purging) purgeMut.mutate(purging.employee_id); }}
        message={purging && (
          <div className="space-y-2">
            <p className="text-danger-text">
              この操作は取り消せません。社員情報・所属履歴が完全に削除され、
              ログインアカウントが連携済みの場合はそのアカウントも無効化されます。
            </p>
            <p>
              通常の退職者にはこの操作を使わず「退職処理」をご利用ください。
              誤って登録した社員を取り消す場合のみ使用してください。
            </p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">氏名：</span><span className="font-medium">{purging.family_name} {purging.given_name}</span></div>
              <div><span className="text-neutral-sub">社員番号：</span><span className="font-mono">{purging.employee_number}</span></div>
              {purging.has_linked_user && (
                <div className="text-danger-text">この社員に連携するログインアカウントも無効化されます</div>
              )}
            </div>
          </div>
        )}
      />

      {/* 所属編集モーダル (v0.3 §2-6, §5-2)。
          ⚠️ v0.4 追加要件⑩: 中身は EmployeeDetailPage の所属タブと共通の EmployeeAffiliationEditor
          へ集約した (従来は同じ UI が二重実装されており §8-55 型の同期漏れリスクがあった)。
          保存・連動確認・エラー処理はコンポーネント側が持つため、footer はキャンセルのみ。 */}
      <Modal
        open={assigning !== null}
        title={`所属編集：${assigning?.name ?? ''}`}
        onClose={() => setAssigning(null)}
        size="md"
        footer={<Button variant="secondary" onClick={() => setAssigning(null)}>閉じる</Button>}
      >
        {assigning && (
          <EmployeeAffiliationEditor employeeId={assigning.employee_id} onSaved={() => setAssigning(null)} />
        )}
      </Modal>
    </div>
  );
}
